import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, TrendingUp, Zap, Target, CalendarHeart, DollarSign, Users } from "lucide-react";

type Momentum = {
  trend_score: number; status: string; support_velocity: number;
  mission_activity: number; livestream_engagement: number;
  referral_growth: number; earnings_growth_cents: number;
};

export default function CreatorMomentum() {
  const { user } = useAuth();
  const [m, setM] = useState<Momentum | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase.from("creator_momentum").select("*").eq("creator_id", user.id).maybeSingle();
      setM(data as any);
      setLoading(false);
    })();
  }, [user]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost"><Link to="/creator-mode"><ArrowLeft className="size-5" /></Link></Button>
          <h1 className="text-xl font-bold">Momentum</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {loading ? <p className="text-center text-muted-foreground py-12">Loading…</p>
          : !m ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <TrendingUp className="size-10 mx-auto mb-2" />
              No momentum data yet. Engage your fans and check back.
            </CardContent></Card>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-3xl">{m.trend_score}</CardTitle>
                      <p className="text-xs text-muted-foreground">Trend score</p>
                    </div>
                    <Badge variant="secondary" className="text-base px-3">{m.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent><Progress value={Math.min(100, m.trend_score)} className="h-3" /></CardContent>
              </Card>
              <div className="grid grid-cols-2 gap-3">
                <Stat icon={Zap} label="Support velocity" value={`${m.support_velocity.toFixed(1)}/d`} />
                <Stat icon={Target} label="Mission activity" value={`${m.mission_activity}`} />
                <Stat icon={CalendarHeart} label="Live engagement" value={`${m.livestream_engagement}`} />
                <Stat icon={Users} label="Referral growth" value={`+${m.referral_growth}`} />
                <Stat icon={DollarSign} label="Earnings growth" value={`$${(m.earnings_growth_cents / 100).toFixed(2)}`} />
              </div>
            </>
          )}
      </main>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }: { icon: any; label: string; value: string }) => (
  <Card><CardContent className="p-4 space-y-1">
    <Icon className="size-4 text-primary" />
    <div className="text-xl font-bold">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </CardContent></Card>
);
